import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';

import CheckboxToggle from '@/components/fields/CheckboxToggle';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import CountryDropdownSelect from '@/views/fields/CountryDropdownSelect/CountryDropdownSelect';
import OfficeDropdownSelect from '@/views/fields/OfficeDropdownSelect/OfficeDropdownSelect';

const normalizeArrayValue = (value: string | string[], shouldBeArray = true) => {
  if (shouldBeArray && !Array.isArray(value)) {
    return [value];
  }

  if (!shouldBeArray && Array.isArray(value)) {
    return value[0];
  }

  return value;
};

const BasicInfoTab: React.FC = () => {
  const { t } = useTranslation('organizations');
  const { watch, setValue, getValues } = useFormContext();
  const isEnterprise = watch('isEnterprise');

  const handleIsEnterpriseChange = useCallback(
    (value: boolean) => {
      const { offices, countries } = getValues();

      setValue('offices', normalizeArrayValue(offices, value));
      setValue('countries', normalizeArrayValue(countries, value));
    },
    [setValue, getValues]
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        p: 4,
        pt: 0,
        '& .MuiFormControl-root': {
          p: 0
        }
      }}
      gap={3}
    >
      <Stack>
        <Field
          label={t('layouts.organizations.create.fields.organizationName')}
          component={TextInput}
          name="name"
          withFormControl
          innerProps={{
            markAsRequired: true,
            placeholder: [
              t('layouts.organizations.create.untitled'),
              t('layouts.organizations.create.fields.organization')
            ].join(' ')
          }}
        />

        <Field
          component={CheckboxToggle}
          name="isEnterprise"
          label={t('layouts.organizations.create.fields.isEnterprise')}
          innerProps={{
            sx: {
              marginInlineStart: -1
            }
          }}
          onChange={handleIsEnterpriseChange}
        />
      </Stack>
      <Box />
      <Field
        component={TextInput}
        name="legalName"
        label={t('layouts.organizations.create.fields.organizationLegalName')}
        withFormControl
      />

      <Field
        component={OfficeDropdownSelect}
        name="offices"
        label={t('layouts.organizations.create.fields.office')}
        markAsRequired
        innerProps={{ isMulti: isEnterprise, usePortal: true }}
        withFormControl
      />

      <Field
        component={CountryDropdownSelect}
        name="countries"
        label={t('layouts.organizations.create.fields.country')}
        markAsRequired
        withFormControl
        innerProps={{
          isMulti: isEnterprise,
          usePortal: true
        }}
      />
    </Box>
  );
};

export default BasicInfoTab;
